import { defineStore } from 'pinia'
import gql from 'graphql-tag'
import apolloClient from "../plugins/apollo"
import { logError } from '@/utils/logger'
import { DEFAULT_REGION_ID } from '@/constants'

export const useAuth = defineStore('auth', {

    state: () => ({
        viewer: 'AnonymousViewer',
        region: { id: '', name: '' },
        userName: '',
        userEmail: '',
        userPhone: '',
        startCheckout: false,
        isLogin: false,
        isLogout: false,
        isAuth: false
      }),

    actions: {
      logInGuest(regionId) {
        apolloClient.mutate({
            mutation: gql `mutation logInAsGuest($regionId: ID!) {
              logInAsGuest(regionId: $regionId) {
                __typename
                ...on LogInAsGuestSuccessResult {
                  accessToken
                  refreshToken
                }
              }
            }`,
            variables: {
              regionId: regionId
            }
          })
          .then((response) => {
            if (!response || !response.data || !response.data.logInAsGuest) {
              logError('Invalid response from logInAsGuest')
              this._setAnonymousState()
              return;
            }
            
            if (response.data.logInAsGuest.__typename !== 'LogInAsGuestSuccessResult') {
              logError('LogInAsGuest failed')
              this._setAnonymousState()
              return;
            }
            
            const { accessToken, refreshToken } = response.data.logInAsGuest;
            
            if (!accessToken || !refreshToken) {
              logError('Missing tokens in logInAsGuest response')
              return;
            }
            
            try {
              localStorage.setItem('token', 'Bearer ' + accessToken);
              localStorage.setItem('refresh', refreshToken);
              localStorage.setItem('isLogin', '0');
            } catch (error) {
              logError('Failed to save tokens to localStorage', error)
              this._setAnonymousState()
              return;
            }
            
            apolloClient.resetStore().then(() => {
              this.isLogin = false;
              this.getViewer();
              if(this.isLogout) {
                this.isLogout = false;
              }
              // Используем window.location вместо router для избежания циклических зависимостей
              window.location.href = '/';
            });
          })
          .catch((error) => {
            logError('Failed to log in as guest', error)
            this._setAnonymousState()
          });
      },
        async refreshToken() {
          let refreshTokenValue = '';
          try {
            refreshTokenValue = localStorage.getItem('refresh') || '';
          } catch (error) {
            logError('Failed to access localStorage', error);
            this._handleRefreshFailure();
            return;
          }

          if (!refreshTokenValue) {
            logError('No refresh token available');
            this._handleRefreshFailure();
            return;
          }

          try {
            const { data } = await apolloClient.mutate({
              mutation: gql`mutation refreshAccessToken($refreshToken: String!) {
                refreshAccessToken(refreshToken: $refreshToken) {
                  __typename
                  ...on RefreshAccessTokenSuccessResult {
                    accessToken
                    refreshToken
                  }
                }
              }`,
              variables: {
                refreshToken: refreshTokenValue
              }
            });

            if (!data || !data.refreshAccessToken) {
              logError('Invalid response from refreshAccessToken');
              this._handleRefreshFailure();
              return;
            }

            if (data.refreshAccessToken.__typename !== 'RefreshAccessTokenSuccessResult') {
              logError('RefreshAccessToken failed');
              this._handleRefreshFailure();
              return;
            }

            const { accessToken, refreshToken: newRefreshToken } = data.refreshAccessToken;

            if (!accessToken || !newRefreshToken) {
              logError('Missing tokens in refreshAccessToken response');
              this._handleRefreshFailure();
              return;
            }

            try {
              localStorage.setItem('token', 'Bearer ' + accessToken);
              localStorage.setItem('refresh', newRefreshToken);
            } catch (error) {
              logError('Failed to save tokens to localStorage', error);
              this._handleRefreshFailure();
              return;
            }

            await apolloClient.resetStore();
            window.location.reload();
          } catch (error) {
            logError('Failed to refresh token', error);
            this._handleRefreshFailure();
          }
        },
        _handleRefreshFailure() {
          const fallbackRegionId = this.region && this.region.id ? this.region.id : DEFAULT_REGION_ID;
          this._clearStoredTokens();
          this._setAnonymousState();
          if (fallbackRegionId) {
            this.logInGuest(fallbackRegionId);
          }
        },
        _clearStoredTokens() {
          try {
            localStorage.setItem('token', '');
            localStorage.setItem('refresh', '');
          } catch (error) {
            logError('Failed to clear stored tokens', error);
          }
        },
        getViewer() {
          apolloClient.query({
            query: gql`query {
              viewer {
                __typename
                ...on AnonymousViewer {
                  region {
                    id
                    name
                  }
                }
                ...on RegisteredViewer {
                  region {
                    id
                    name
                  }
                  name
                  emailAddress
                  phoneNumber
                }
              }
            }`
          }).then((response) => {
            if (!response || !response.data || !response.data.viewer) {
              logError('Invalid response from getViewer')
              try {
                localStorage.setItem('isLogin', '0');
              } catch (error) {
                logError('Failed to set isLogin', error)
              }
              this._setAnonymousState()
              return;
            }
            
            const viewer = response.data.viewer;
            const viewerType = viewer.__typename || 'AnonymousViewer';
            this.viewer = viewerType;
            this.region = viewer.region || { id: '', name: '' };
            
            if(viewerType === 'RegisteredViewer') {
              try {
                localStorage.setItem('isLogin', '1');
              } catch (error) {
                logError('Failed to set isLogin', error)
              }
              this.userName = viewer.name || '';
              this.userEmail = viewer.emailAddress || '';
              this.userPhone = viewer.phoneNumber || '';
              this.isLogin = true;
            } else {
              this._setAnonymousState()
            }
          }).catch((error) => {
            logError('Failed to get viewer', error)
            // fallback: ensure state reflects anonymous viewer
            this._setAnonymousState()
          })  
        },
        logOut() {
          apolloClient.mutate({
            mutation: gql `mutation {
              logOut {
                __typename
              }
            }`
          })
          .then((response) => {
            if (!response || !response.data || !response.data.logOut) {
              logError('Invalid response from logOut')
              return;
            }
            
            if(response.data.logOut.__typename == "LogOutSuccessResult") {
              try {
                localStorage.setItem('token','');
                localStorage.setItem('refresh','');
                localStorage.setItem('isLogin', '0');
              } catch (error) {
                logError('Failed to clear localStorage', error)
              }
              this._setAnonymousState()
              window.location.href = "/";
            }
          })
          .catch((error) => {
            logError('Failed to log out', error)
            this._setAnonymousState()
          })
        },
        checkViewer() {
          apolloClient.query({
            query: gql`query {
              viewer {
                __typename
              }
            }`
          })
          .then(() =>{
          });
          
        },
        _setAnonymousState() {
          this.viewer = 'AnonymousViewer'
          this.region = this.region && this.region.id ? this.region : { id: '', name: '' }
          this.userName = ''
          this.userEmail = ''
          this.userPhone = ''
          this.isLogin = false
          try {
            localStorage.setItem('isLogin', '0');
          } catch (error) {
            logError('Failed to set isLogin', error)
          }
        },
        /**
         * Сохраняет токены после успешного логина
         * @param {string} accessToken - Токен доступа
         * @param {string} refreshToken - Токен обновления
         */
        saveTokens(accessToken, refreshToken) {
          try {
            localStorage.setItem('token', 'Bearer ' + accessToken);
            localStorage.setItem('refresh', refreshToken);
            localStorage.setItem('isLogin', '1');
            this.isLogin = true;
          } catch (error) {
            logError('Failed to save tokens to localStorage', error);
            throw error;
          }
        }
    }
  })